/**
 * The public presentation page (visitors not signed in), in the spirit of
 * makeit.app: a sticky top bar (sections, language, Log in / Sign up, "I'm a
 * student"), the hero with the code box to play straight away, figures,
 * features with screenshots of the web version, the kinds of activity, videos,
 * playable demos, plans and prices, FAQ, downloads and the footer. Log in and
 * Sign up open AuthPanel in a dialog; once signed in Home shows HomeApp.
 *
 * Screenshots: public/landing/*.webp (taken from the web player and editor
 * with the bundled demos). Texts: landingText.ts.
 */
import { useEffect, useLayoutEffect, useRef, useState, type FormEvent, type ReactNode, type RefObject } from "react";
import type { ProjectSource } from "../data/loadProject";
import { libraryUrl } from "../data/media";
import { AuthError, register, resetPassword, signIn, signInWithApple } from "../data/auth";
import { currentLanguage, saveUiLanguage, t, UI_LANGUAGES } from "../player/i18n";
import { RTL_LANGUAGES } from "../player/rtl";
import { AppleButton, Icon } from "./widgets";
import { slideTypeName } from "./slideNames";
import { LANGUAGE_NAMES, landingText, type LandingText } from "./landingText";
import { annualPrice, packPrice, SHOW_PACKS_KEY } from "./localPrice";
import { audienceText } from "./audienceText";
import { audiencePath } from "../site";
import { asClassCode } from "../data/classCode";
import { hrefOf } from "./router";
import "./landing.css";

interface DemoEntry {
  id: string;
  name: string;
  type: string;
  slides: number;
  /** a translated demo: the demo whose screenshot it shows (tools/src/demo-i18n.ts) */
  shot?: string;
}

/** The languages of the bundled demos (public/demos/<lang>): Unity's two, and those of tools/demos-i18n. */
const DEMO_LANGUAGES = ["en", "es", "ca", "fr", "ar"];
/** The demos shown first: those in the app's language, or else the English ones. */
const demoLanguage = (ui: string) => (DEMO_LANGUAGES.includes(ui) ? ui : "en");

const LANG_NAMES: Record<string, string> = { en: "English", es: "Español", ca: "Català", fr: "Français", pt: "Português", it: "Italiano", de: "Deutsch", ar: "العربية" };

/** The demos of every language, as they load. */
function useDemos(): Record<string, DemoEntry[]> {
  const [demos, setDemos] = useState<Record<string, DemoEntry[]>>({});
  useEffect(() => {
    for (const l of DEMO_LANGUAGES) {
      fetch(`/demos/${l}/index.json`)
        .then((r) => (r.ok ? r.json() : []))
        .then((list: DemoEntry[]) => list.length && setDemos((d) => ({ ...d, [l]: list })))
        .catch(() => undefined);
    }
  }, []);
  return demos;
}

/** Buttons to pick the demos' language. */
function DemoLanguagePicker({ langs, lang, onPick }: { langs: string[]; lang: string; onPick: (l: string) => void }) {
  if (langs.length < 2) return null;
  return (
    <div className="lp-seg lp-seg-small">
      {langs.map((l) => (
        <button key={l} className={l === lang ? "on" : ""} onClick={() => onPick(l)} lang={l}>
          {LANG_NAMES[l] ?? l}
        </button>
      ))}
    </div>
  );
}
const isQuiz = (type: string) => type === "quiz" || type === "quizDemo";
const cleanName = (n: string) => n.replace(/^(DemoGame|DemoQuiz|JuegoDemo|DemoJuego|QuizDemo)\.?\s*/i, "");

export const LINKS = {
  appStore: "https://apps.apple.com/us/app/make-it-crea-juegos-y-quizzes/id1182354738",
  googlePlay: "https://play.google.com/store/apps/details?id=com.planetfactory.makeitAndroid",
  youtube: "https://www.youtube.com/channel/UCIYALFMb_q8O7tSBcQZVCAQ",
  instagram: "https://www.instagram.com/app.makeit/",
  facebook: "https://www.facebook.com/MakeItGamesAndQuizzes/",
  linkedin: "https://www.linkedin.com/company/learnyland",
  // the legal pages of this web (LegalPage), at the old WordPress addresses
  terms: "/terms-and-conditions",
  privacy: "/privacy-policy",
  cookies: "/cookie-policy",
  email: "makeit@learnyland.com",
};

/** "What is Make It?" and the tutorials (makeit.app › FAQ). */
const VIDEOS = { main: "5LqooDnffLY", quiz: "h0u3jB4VDYQ", game: "brbKuD5IvWA", share: "Uu4yzP_FpBs" };

/** Activity cards of the editor's "Add new activity" panel (_Sprites/editing/slides). */
const GAME_TYPES = ["connect", "drag", "memory", "quiz", "fillWithLetters", "math", "tapIt", "sequencesImg", "order", "word", "phrase", "dragText", "traceable", "puzzle", "paint", "simbolDraw", "free"];
const QUIZ_TYPES = ["quizzesQuiz", "trueFalse", "typeAnswer", "fillWithLetters", "math", "word", "missingLetter", "missingWord", "phrase", "free"];
const CARD: Record<string, string> = { quizzesQuiz: "quiz" };

const SECTIONS = ["features", "activities", "videos", "plans", "faq"] as const;

export function Landing({ onPlay, error }: { onPlay: (source: ProjectSource) => void; error?: string }) {
  const L = landingText();
  const A = audienceText();
  const lang = currentLanguage();
  // the plans in the visitor's currency
  const educators = annualPrice();
  const teams = packPrice("teams");
  const schools = packPrice("schools");
  // ?signup (the audience pages' "Start for free"): open the sign-up dialog
  const [auth, setAuth] = useState<null | "login" | "register">(() => (new URLSearchParams(location.search).has("signup") ? "register" : null));
  const [menu, setMenu] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const page = useRef<HTMLDivElement>(null);
  const navIn = useRef<HTMLDivElement>(null);
  const navFit = useNavFit(navIn, lang);
  useReveal(page);
  // after "Delete account" (HomeApp › DeleteAccountDialog): a word that it is done
  const [bye, setBye] = useState(() => {
    try {
      const v = sessionStorage.getItem("makeit.accountDeleted") === "1";
      sessionStorage.removeItem("makeit.accountDeleted");
      return v;
    } catch {
      return false;
    }
  });
  useEffect(() => {
    if (!bye) return;
    const id = window.setTimeout(() => setBye(false), 6000);
    return () => window.clearTimeout(id);
  }, [bye]);
  useEffect(() => {
    const before = document.title;
    document.title = `Make It · ${L.hero.title}`;
    return () => {
      document.title = before;
    };
  }, [L]);

  const go = (id: string) => {
    setMenu(false);
    document.getElementById(`lp-${id}`)?.scrollIntoView({ behavior: "smooth", block: "start" });
  };
  const signup = () => setAuth("register");
  // the Educators plan: sign up (or log in) and land on the Subscription page; HomeApp reads the route from the hash
  const subscribe = () => {
    history.replaceState(null, "", "#/subscription");
    setAuth("register");
  };
  const closeAuth = () => {
    if (location.hash === "#/subscription") history.replaceState(null, "", "#/");
    setAuth(null);
  };
  // the school packs: the same, and the Subscription page shows them first
  const buyPack = () => {
    try {
      sessionStorage.setItem(SHOW_PACKS_KEY, "1");
    } catch {
      /* the page opens at the top */
    }
    subscribe();
  };

  return (
    <div className="lp" ref={page} dir={RTL_LANGUAGES.has(lang) ? "rtl" : "ltr"} lang={lang} onScroll={(e) => setScrolled(e.currentTarget.scrollTop > 10)}>
      {/* ---- top bar ---- */}
      <header className={`lp-nav ${scrolled ? "scrolled" : ""} ${navFit > 0 ? "lp-nav-tight" : ""} ${navFit > 1 ? "lp-nav-compact" : ""}`}>
        <div className="lp-wrap lp-nav-in" ref={navIn}>
          <button className="lp-logo" onClick={() => page.current?.scrollTo({ top: 0, behavior: "smooth" })} aria-label="Make It">
            <img src={libraryUrl("ui/logo.png")} alt="Make It" />
          </button>
          <nav className={`lp-links ${menu ? "open" : ""}`} aria-label={L.nav.menu}>
            {SECTIONS.map((id) => (
              <button key={id} className="lp-link" onClick={() => go(id)}>
                {L.nav[id]}
              </button>
            ))}
            <a className="lp-link lp-link-student" href="#/student">
              {L.nav.student}
            </a>
            <div className="lp-links-auth">
              <button className="lp-btn lp-btn-ghost" onClick={() => (setMenu(false), setAuth("login"))}>
                {L.nav.login}
              </button>
              <button className="lp-btn lp-btn-primary" onClick={() => (setMenu(false), signup())}>
                {L.nav.signup}
              </button>
            </div>
          </nav>
          <div className="lp-actions">
            <LanguagePicker label={L.nav.language} />
            <a className="lp-link lp-student" href="#/student">
              {L.nav.student}
            </a>
            <button className="lp-btn lp-btn-ghost lp-hide-s" onClick={() => setAuth("login")}>
              {L.nav.login}
            </button>
            <button className="lp-btn lp-btn-primary lp-hide-s" onClick={signup}>
              {L.nav.signup}
            </button>
            <button className={`lp-burger ${menu ? "open" : ""}`} onClick={() => setMenu((m) => !m)} aria-label={L.nav.menu} aria-expanded={menu}>
              <span />
              <span />
              <span />
            </button>
          </div>
        </div>
      </header>

      {bye && (
        <div className="lp-toast" role="status" onClick={() => setBye(false)}>
          {t("accountDeleted")}
        </div>
      )}
      <main>
        {/* ---- hero ---- */}
        <section className="lp-hero">
          <Confetti />
          <div className="lp-wrap lp-hero-in">
            <div className="lp-hero-text">
              <span className="lp-eyebrow">{L.hero.eyebrow}</span>
              <h1>{L.hero.title}</h1>
              <p className="lp-lead">{L.hero.sub}</p>
              <div className="lp-cta">
                <button className="lp-btn lp-btn-primary lp-btn-big" onClick={signup}>
                  {L.hero.start}
                </button>
                <button className="lp-btn lp-btn-outline lp-btn-big" onClick={() => go("demos")}>
                  {L.hero.demo}
                </button>
              </div>
              <p className="lp-note">{L.hero.free}</p>
              <CodeCard L={L} onPlay={onPlay} error={error} />
            </div>
            <div className="lp-hero-art" aria-hidden>
              <Browser className="lp-hero-editor" src="/landing/editor.webp" />
              <Tablet className="lp-hero-tablet" src="/landing/game-colours.webp" />
            </div>
          </div>
        </section>

        {/* ---- figures (makeit.app › What is Make It) ---- */}
        <section className="lp-stats">
          <div className="lp-wrap lp-stats-in">
            <Stat value={`+${compact(1_000_000, lang)}`} label={L.stats.downloads} />
            <Stat value={number(350_000, lang)} label={L.stats.users} />
            <Stat value={number(700_000, lang)} label={L.stats.projects} />
            <Stat value={number(170, lang)} label={L.stats.countries} />
          </div>
        </section>

        {/* ---- features at a glance ---- */}
        <section className="lp-section" id="lp-features">
          <div className="lp-wrap">
            <Heading title={L.grid.title} sub={L.grid.sub} />
            <div className="lp-grid">
              {L.grid.items.map((it, i) => (
                <div key={i} className={`lp-tile lp-reveal tint-${i % 4}`}>
                  <span className="lp-tile-icon">
                    <Icon name={["templates", "games", "share", "ranking", "class", "online"][i]} />
                  </span>
                  <h3>{it.title}</h3>
                  <p>{it.text}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ---- create ---- */}
        <Feature tint="purple" kicker={L.create.kicker} title={L.create.title} text={L.create.text} bullets={L.create.bullets} art={<Browser src="/landing/editor-emotions.webp" />} />

        {/* ---- games ---- */}
        <Feature
          tint="orange"
          flip
          kicker={L.play.kicker}
          title={L.play.title}
          text={L.play.text}
          bullets={L.play.bullets}
          art={
            <div className="lp-stack">
              <Tablet className="lp-stack-a" src="/landing/game-shapes.webp" />
              <Tablet className="lp-stack-b" src="/landing/game-letters.webp" />
              <Tablet className="lp-stack-c" src="/landing/game-body.webp" />
            </div>
          }
        />

        {/* ---- quizzes ---- */}
        <Feature
          tint="red"
          kicker={L.quiz.kicker}
          title={L.quiz.title}
          text={L.quiz.text}
          bullets={L.quiz.bullets}
          art={
            <div className="lp-quiz-art">
              <Tablet src="/landing/quiz.webp" />
              <div className="lp-rank" aria-hidden>
                {[
                  ["Anna", 980],
                  ["Marc", 910],
                  ["Laia", 860],
                  ["Pau", 720],
                ].map(([n, p], i) => (
                  <div key={i} className="lp-rank-row">
                    <span className="lp-rank-pos">{i + 1}</span>
                    <span className="lp-rank-name">{n}</span>
                    <span className="lp-rank-pts">{number(p as number, lang)}</span>
                  </div>
                ))}
              </div>
            </div>
          }
        />

        {/* ---- share ---- */}
        <Feature
          tint="blue"
          flip
          kicker={L.share.kicker}
          title={L.share.title}
          text={L.share.text}
          art={
            <div className="lp-share-art">
              <div className="lp-share-card">
                <span className="lp-share-label">{L.share.code}</span>
                <span className="lp-share-code">482 913</span>
                <span className="lp-share-label">{L.share.link}</span>
                <span className="lp-share-link">play.makeit.app?code=482913</span>
                <span className="lp-share-class">
                  <Icon name="class" /> {L.share.classroom}
                </span>
              </div>
              <Tablet className="lp-share-tablet" src="/landing/game-emotions.webp" />
            </div>
          }
        />

        {/* ---- classrooms ---- */}
        <Feature
          tint="green"
          kicker={L.classes.kicker}
          title={L.classes.title}
          text={L.classes.text}
          bullets={L.classes.bullets}
          art={
            <div className="lp-results" aria-hidden>
              <div className="lp-results-top">
                <Icon name="class" /> {L.classes.mockTitle}
              </div>
              <div className="lp-results-head">
                {L.classes.mockCols.map((c) => (
                  <span key={c}>{c}</span>
                ))}
              </div>
              {[
                ["Anna", 100, "02:41"],
                ["Marc", 92, "03:05"],
                ["Laia", 85, "02:58"],
                ["Pau", 77, "04:12"],
                ["Júlia", 69, "03:37"],
              ].map(([n, s, tm]) => (
                <div key={n as string} className="lp-results-row">
                  <span>{n}</span>
                  <span className="lp-results-bar">
                    <i style={{ width: `${s}%` }} />
                    <b>{s}</b>
                  </span>
                  <span>{tm}</span>
                </div>
              ))}
            </div>
          }
        />

        {/* ---- activities ---- */}
        <Activities L={L} lang={lang} />

        {/* ---- videos ---- */}
        <section className="lp-section lp-videos" id="lp-videos">
          <div className="lp-wrap">
            <Heading title={L.videos.title} sub={L.videos.sub} />
            <div className="lp-video-main lp-reveal">
              <YouTube id={VIDEOS.main} title={L.videos.main} big />
            </div>
            <h3 className="lp-subhead">{L.videos.tutorials}</h3>
            <div className="lp-video-row">
              <YouTube id={VIDEOS.quiz} title={L.videos.quiz} />
              <YouTube id={VIDEOS.game} title={L.videos.game} />
              <YouTube id={VIDEOS.share} title={L.videos.share} />
            </div>
            <p className="lp-center">
              <a className="lp-more" href={LINKS.youtube} target="_blank" rel="noreferrer">
                {L.videos.all} →
              </a>
            </p>
          </div>
        </section>

        {/* ---- demos ---- */}
        <section className="lp-section lp-demos" id="lp-demos">
          <div className="lp-wrap">
            <Heading title={L.demos.title} sub={L.demos.sub} />
            <DemoCards L={L} onPlay={onPlay} />
          </div>
        </section>

        {/* ---- Make It for… (AudiencePage) ---- */}
        <section className="lp-section lp-audiences">
          <div className="lp-wrap">
            <Heading title={A.forTitle} sub={A.forSub} />
            <div className="lp-audience-cards">
              {(["schools", "special"] as const).map((a) => (
                <a key={a} className={`lp-audience-card lp-reveal tint-${a === "schools" ? 2 : 1}`} href={audiencePath(a)}>
                  <span className="lp-tile-icon">
                    <Icon name={a === "schools" ? "class" : "reinforcement"} />
                  </span>
                  <strong>{a === "schools" ? A.schools : A.special}</strong>
                  <span>{a === "schools" ? A.schoolsLine : A.specialLine}</span>
                  <em>{A.more} →</em>
                </a>
              ))}
            </div>
          </div>
        </section>

        {/* ---- plans ---- */}
        <section className="lp-section lp-plans" id="lp-plans">
          <div className="lp-wrap">
            <Heading title={L.plans.title} sub={L.plans.sub} />
            <div className="lp-plan-grid">
              <Plan name={L.plans.free} price={money(0, lang, educators.currency)} features={L.plans.freeFeatures} cta={L.plans.startFree} onCta={signup} />
              <Plan name={L.plans.educators} price={money(educators.amount, lang, educators.currency)} per={L.plans.perYear} features={L.plans.educatorsFeatures} cta={L.plans.tryFree} onCta={subscribe} accent />
              <Plan name={L.plans.teams} price={money(teams.amount, lang, teams.currency)} per={L.plans.perYear} features={L.plans.teamsFeatures} cta={L.plans.subscribe} onCta={buyPack} />
              <Plan name={L.plans.schools} price={money(schools.amount, lang, schools.currency)} per={L.plans.perYear} features={L.plans.schoolsFeatures} cta={L.plans.subscribe} onCta={buyPack} />
            </div>
            <p className="lp-center lp-note">{L.plans.note}</p>
          </div>
        </section>

        {/* ---- FAQ ---- */}
        <section className="lp-section lp-faq" id="lp-faq">
          <div className="lp-wrap lp-faq-in">
            <Heading title={L.faq.title} sub={L.faq.sub} />
            <div className="lp-faq-list">
              {L.faq.items.map((it, i) => (
                <details key={i} className="lp-qa">
                  <summary>{it.q}</summary>
                  <p>{it.a}</p>
                </details>
              ))}
            </div>
          </div>
        </section>

        {/* ---- download ---- */}
        <section className="lp-section lp-download" id="lp-download">
          <div className="lp-wrap">
            <Heading title={L.download.title} sub={L.download.sub} />
            <div className="lp-stores">
              <button className="lp-store lp-store-web" onClick={signup}>
                <StoreIcon kind="web" />
                <span>
                  <small>Make It</small>
                  {L.download.web}
                </span>
              </button>
              <StoreBadge href={LINKS.appStore} kind="app-store" name="App Store" lang={lang} />
              <StoreBadge href={LINKS.appStore} kind="mac-app-store" name="Mac App Store" lang={lang} />
              <StoreBadge href={LINKS.googlePlay} kind="google-play" name="Google Play" lang={lang} />
            </div>
          </div>
        </section>

        {/* ---- last call ---- */}
        <section className="lp-final">
          <Confetti light />
          <div className="lp-wrap lp-final-in">
            <h2>{L.final.title}</h2>
            <p>{L.final.sub}</p>
            <div className="lp-cta">
              <button className="lp-btn lp-btn-white lp-btn-big" onClick={signup}>
                {L.hero.start}
              </button>
              <button className="lp-btn lp-btn-clear lp-btn-big" onClick={() => setAuth("login")}>
                {L.nav.login}
              </button>
            </div>
          </div>
        </section>
      </main>

      {/* ---- footer ---- */}
      <footer className="lp-foot">
        <div className="lp-wrap lp-foot-in">
          <div className="lp-foot-brand">
            <img src={libraryUrl("ui/logo.png")} alt="Make It" />
            <p>{L.footer.tagline}</p>
            <p className="lp-foot-support">{L.footer.support}</p>
          </div>
          <div className="lp-foot-col">
            <h4>{L.footer.product}</h4>
            <button onClick={() => go("features")}>{L.nav.features}</button>
            <button onClick={() => go("activities")}>{L.nav.activities}</button>
            <button onClick={() => go("demos")}>{L.footer.demos}</button>
            <button onClick={() => go("plans")}>{L.nav.plans}</button>
          </div>
          <div className="lp-foot-col">
            <h4>{A.forTitle}</h4>
            <a href={audiencePath("schools")}>{A.schools}</a>
            <a href={audiencePath("special")}>{A.special}</a>
          </div>
          <div className="lp-foot-col">
            <h4>{L.footer.help}</h4>
            <button onClick={() => go("faq")}>{L.nav.faq}</button>
            <button onClick={() => go("videos")}>{L.videos.tutorials}</button>
            <a href={`mailto:${LINKS.email}`}>{L.footer.contact}</a>
            <a href="#/student">{L.nav.student}</a>
          </div>
          <div className="lp-foot-col">
            <h4>{L.footer.follow}</h4>
            <a href={LINKS.instagram} target="_blank" rel="noreferrer">
              Instagram
            </a>
            <a href={LINKS.youtube} target="_blank" rel="noreferrer">
              YouTube
            </a>
            <a href={LINKS.facebook} target="_blank" rel="noreferrer">
              Facebook
            </a>
            <a href={LINKS.linkedin} target="_blank" rel="noreferrer">
              LinkedIn
            </a>
          </div>
        </div>
        <div className="lp-wrap lp-foot-bottom">
          <span>© {new Date().getFullYear()} Learny Land · Planet Factory</span>
          <span>
            <a href={LINKS.terms} target="_blank" rel="noreferrer">
              {L.footer.terms}
            </a>
            <a href={LINKS.privacy} target="_blank" rel="noreferrer">
              {L.footer.privacy}
            </a>
            <a href={LINKS.cookies} target="_blank" rel="noreferrer">
              {L.footer.cookies}
            </a>
            <a href={`mailto:${LINKS.email}`}>{LINKS.email}</a>
          </span>
        </div>
      </footer>

      {auth && <AuthDialog mode={auth} onMode={setAuth} onClose={closeAuth} />}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Pieces
// ---------------------------------------------------------------------------

function number(n: number, lang: string): string {
  try {
    return new Intl.NumberFormat(lang).format(n);
  } catch {
    return String(n);
  }
}
function compact(n: number, lang: string): string {
  try {
    return new Intl.NumberFormat(lang, { notation: "compact" }).format(n);
  } catch {
    return String(n);
  }
}
function money(n: number, lang: string, currency: string): string {
  try {
    return new Intl.NumberFormat(lang, { style: "currency", currency, minimumFractionDigits: Number.isInteger(n) ? 0 : 2 }).format(n);
  } catch {
    return `${n} ${currency}`;
  }
}

/**
 * How much the top bar has to shrink to fit in one row (long texts in some
 * languages, or a narrow window): 0 as it is, 1 without the student link and
 * with tighter links (as under 1100px), 2 with the links in the menu button.
 */
function useNavFit(bar: RefObject<HTMLDivElement | null>, lang: string): number {
  const [fit, setFit] = useState(0);
  useLayoutEffect(() => {
    const el = bar.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const need = [0, 0]; // the width the bar needed at each step, measured when it did not fit
    let step = 0;
    let frame = 0;
    const set = (n: number) => {
      step = n;
      setFit(n);
      frame = requestAnimationFrame(check); // look again once the new layout is in
    };
    const check = () => {
      const least = matchMedia("(max-width: 960px)").matches ? 2 : 0;
      if (step < least) set(least);
      else if (step < 2 && el.scrollWidth > el.clientWidth + 1) {
        need[step] = el.scrollWidth;
        set(step + 1);
      } else if (step > least && el.clientWidth >= need[step - 1]) set(step - 1);
    };
    setFit(0);
    const ro = new ResizeObserver(check);
    ro.observe(el);
    // the links and buttons too: they grow when the web fonts arrive, the bar does not
    for (const child of el.children) ro.observe(child);
    return () => {
      ro.disconnect();
      cancelAnimationFrame(frame);
    };
  }, [bar, lang]);
  return fit;
}

/** Sections fade in as they come into view. */
export function useReveal(root: RefObject<HTMLDivElement | null>) {
  useEffect(() => {
    const el = root.current;
    if (!el || typeof IntersectionObserver === "undefined") return;
    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting) {
            e.target.classList.add("in");
            io.unobserve(e.target);
          }
        }
      },
      { root: el, rootMargin: "0px 0px -8% 0px", threshold: 0.08 },
    );
    el.querySelectorAll(".lp-reveal").forEach((n) => io.observe(n));
    return () => io.disconnect();
  }, [root]);
}

export function Heading({ title, sub }: { title: string; sub?: string }) {
  return (
    <header className="lp-heading lp-reveal">
      <h2>{title}</h2>
      {sub && <p>{sub}</p>}
    </header>
  );
}

function Stat({ value, label }: { value: string; label: string }) {
  return (
    <div className="lp-stat">
      <strong>{value}</strong>
      <span>{label}</span>
    </div>
  );
}

export function Feature({ tint, flip, kicker, title, text, bullets, art }: { tint: string; flip?: boolean; kicker: string; title: string; text: string; bullets?: string[]; art: ReactNode }) {
  return (
    <section className={`lp-feature tint-${tint} ${flip ? "flip" : ""}`}>
      <div className="lp-wrap lp-feature-in">
        <div className="lp-feature-text lp-reveal">
          <span className="lp-kicker">{kicker}</span>
          <h2>{title}</h2>
          <p>{text}</p>
          {bullets && (
            <ul className="lp-ticks">
              {bullets.map((b) => (
                <li key={b}>{b}</li>
              ))}
            </ul>
          )}
        </div>
        <div className="lp-feature-art lp-reveal">{art}</div>
      </div>
    </section>
  );
}

/** A desktop browser window around a screenshot. */
export function Browser({ src, className }: { src: string; className?: string }) {
  return (
    <div className={`lp-browser ${className ?? ""}`}>
      <div className="lp-browser-bar">
        <i />
        <i />
        <i />
        <span>{location.host}</span>
      </div>
      <img src={src} alt="" loading="lazy" decoding="async" />
    </div>
  );
}

/** A tablet around a 4:3 screenshot. */
export function Tablet({ src, className }: { src: string; className?: string }) {
  return (
    <div className={`lp-tablet ${className ?? ""}`}>
      <img src={src} alt="" loading="lazy" decoding="async" />
    </div>
  );
}

/** The confetti of the app's splash. */
export function Confetti({ light }: { light?: boolean }) {
  const ui = (n: string) => libraryUrl(`ui/${n}.png`);
  return (
    <div className={`lp-confetti ${light ? "light" : ""}`} aria-hidden>
      <img src={ui("decoBlue")} alt="" style={{ insetInlineStart: "4%", top: "14%", width: 46, transform: "rotate(137deg)" }} />
      <img src={ui("decoViolet")} alt="" style={{ insetInlineEnd: "6%", top: "8%", width: 52, transform: "rotate(-60deg)" }} />
      <img src={ui("decoRed")} alt="" style={{ insetInlineStart: "45%", top: "6%", width: 22 }} />
      <img src={ui("dotYellow")} alt="" style={{ insetInlineEnd: "40%", bottom: "10%", width: 20 }} />
      <img src={ui("decoBlue")} alt="" style={{ insetInlineEnd: "3%", bottom: "16%", width: 36, transform: "rotate(-90deg)" }} />
      <img src={ui("decoViolet")} alt="" style={{ insetInlineStart: "2%", bottom: "8%", width: 40, transform: "rotate(-149deg)" }} />
    </div>
  );
}

export function CodeCard({ L, onPlay, error }: { L: LandingText; onPlay: (s: ProjectSource) => void; error?: string }) {
  const [code, setCode] = useState("");
  const submit = (e: FormEvent) => {
    e.preventDefault();
    const c = code.trim();
    // six letters: a classroom's code (a project's is six digits), so the student's join page
    const room = asClassCode(c, true);
    if (room) location.hash = hrefOf({ name: "student", code: room });
    else if (c) onPlay({ kind: "code", code: c });
  };
  return (
    <form className="lp-code" onSubmit={submit}>
      <div className="lp-code-text">
        <strong>{L.hero.codeTitle}</strong>
        <span>{L.hero.codeText}</span>
      </div>
      <div className="lp-code-row">
        <input className="code-input" value={code} onChange={(e) => setCode(e.target.value)} placeholder={t("code")} autoCapitalize="characters" autoComplete="off" inputMode="numeric" aria-label={t("code")} />
        <button className="play-btn" type="submit" disabled={!code.trim()} aria-label={t("play")} style={{ backgroundImage: `url(${libraryUrl("ui/play.png")})` }} />
      </div>
      {error && <p className="error">{error}</p>}
    </form>
  );
}

function Activities({ L, lang }: { L: LandingText; lang: string }) {
  const [kind, setKind] = useState<"game" | "quiz">("game");
  const types = kind === "game" ? GAME_TYPES : QUIZ_TYPES;
  return (
    <section className="lp-section lp-activities" id="lp-activities">
      <div className="lp-wrap">
        <Heading title={L.activities.title} sub={L.activities.sub} />
        <div className="lp-seg" role="tablist">
          <button role="tab" aria-selected={kind === "game"} className={kind === "game" ? "on" : ""} onClick={() => setKind("game")}>
            <Icon name="games" /> {L.activities.games}
          </button>
          <button role="tab" aria-selected={kind === "quiz"} className={kind === "quiz" ? "on" : ""} onClick={() => setKind("quiz")}>
            <Icon name="quizzes" /> {L.activities.quizzes}
          </button>
        </div>
        <div className="lp-acts">
          {types.map((ty) => (
            <figure key={ty} className="lp-act">
              <img src={libraryUrl(`ui/slide-${CARD[ty] ?? ty}.png`)} alt="" loading="lazy" />
              <figcaption>{slideTypeName(ty, lang)}</figcaption>
            </figure>
          ))}
        </div>
      </div>
    </section>
  );
}

/** A YouTube video that only loads YouTube when it is played. */
function YouTube({ id, title, big }: { id: string; title: string; big?: boolean }) {
  const [on, setOn] = useState(false);
  return (
    <figure className={`lp-yt ${big ? "big" : ""}`}>
      <div className="lp-yt-box">
        {on ? (
          <iframe src={`https://www.youtube-nocookie.com/embed/${id}?autoplay=1&rel=0`} title={title} allow="autoplay; encrypted-media; picture-in-picture; fullscreen" allowFullScreen />
        ) : (
          <button className="lp-yt-facade" onClick={() => setOn(true)} aria-label={title} style={{ backgroundImage: `url(https://i.ytimg.com/vi/${id}/hqdefault.jpg)` }}>
            <span className="lp-yt-play" />
          </button>
        )}
      </div>
      <figcaption>{title}</figcaption>
    </figure>
  );
}

export function DemoCards({ L, onPlay }: { L: LandingText; onPlay: (s: ProjectSource) => void }) {
  const [lang, setLang] = useState(() => demoLanguage(currentLanguage()));
  const demos = useDemos();
  const list = demos[lang] ?? [];
  const langs = DEMO_LANGUAGES.filter((l) => demos[l]);
  return (
    <>
      <DemoLanguagePicker langs={langs} lang={lang} onPick={setLang} />
      {(["game", "quiz"] as const).map((kind) => {
        const items = list.filter((d) => (kind === "quiz") === isQuiz(d.type));
        if (!items.length) return null;
        return (
          <div key={kind} className="lp-demo-group">
            <h3 className="lp-subhead">
              <span className={`lp-dot ${kind}`} /> {kind === "game" ? L.demos.games : L.demos.quizzes}
            </h3>
            <div className="lp-demo-grid">
              {items.map((d) => (
                <button key={d.id} className="lp-demo" onClick={() => onPlay({ kind: "demo", lang, id: d.id })}>
                  <span className="lp-demo-shot">
                    <img src={`/landing/demos/${d.shot ?? d.id}.webp`} alt="" loading="lazy" />
                    <span className="lp-demo-play">{L.demos.play}</span>
                  </span>
                  <span className="lp-demo-name" dir="auto">{cleanName(d.name)}</span>
                  <span className="lp-demo-meta">
                    {d.slides} {L.demos.pages}
                  </span>
                </button>
              ))}
            </div>
          </div>
        );
      })}
    </>
  );
}

function Plan({ name, price, per, features, cta, onCta, accent }: { name: string; price: string; per?: string; features: string[]; cta: string; onCta: () => void; accent?: boolean }) {
  return (
    <div className={`lp-plan lp-reveal ${accent ? "accent" : ""}`}>
      <h3>{name}</h3>
      <p className="lp-price">
        <strong>{price}</strong>
        {per && <span>{per}</span>}
      </p>
      <ul className="lp-ticks">
        {features.map((f) => (
          <li key={f}>{f}</li>
        ))}
      </ul>
      <button className={`lp-btn ${accent ? "lp-btn-primary" : "lp-btn-outline"}`} onClick={onCta}>
        {cta}
      </button>
    </div>
  );
}

/**
 * The stores' official badges, in the page's language (public/badges, from
 * Apple's marketing tools and Google Play's badge page; English where a store
 * has none). They are shown as they come: no recolouring or cropping.
 */
const BADGE_LANGS = new Set(["ca", "es", "en", "fr", "pt", "it", "de", "ar", "he"]);

function StoreBadge({ href, kind, name, lang }: { href: string; kind: "app-store" | "mac-app-store" | "google-play"; name: string; lang: string }) {
  const l = BADGE_LANGS.has(lang) ? lang : "en";
  const src = `/badges/${kind}-${l}.${kind === "google-play" ? "png" : "svg"}`;
  return (
    <a className={`lp-badge lp-badge-${kind}`} href={href} target="_blank" rel="noreferrer">
      <img src={src} alt={name} loading="lazy" />
    </a>
  );
}

/** Plain device glyphs (no store logos). */
function StoreIcon({ kind }: { kind: "web" }) {
  const common = { width: 26, height: 26, viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: 1.8, strokeLinecap: "round" as const, strokeLinejoin: "round" as const, "aria-hidden": true };
  switch (kind) {
    case "web":
      return (
        <svg {...common}>
          <circle cx="12" cy="12" r="9" />
          <path d="M3 12h18M12 3c2.5 2.6 3.8 5.6 3.8 9s-1.3 6.4-3.8 9c-2.5-2.6-3.8-5.6-3.8-9S9.5 5.6 12 3z" />
        </svg>
      );
  }
}

export function LanguagePicker({ label }: { label: string }) {
  const cur = currentLanguage();
  return (
    <label className="lp-lang">
      <span className="sr-only">{label}</span>
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden>
        <circle cx="12" cy="12" r="9" />
        <path d="M3 12h18M12 3c2.5 2.6 3.8 5.6 3.8 9s-1.3 6.4-3.8 9c-2.5-2.6-3.8-5.6-3.8-9S9.5 5.6 12 3z" />
      </svg>
      <select
        value={cur}
        aria-label={label}
        onChange={(e) => {
          saveUiLanguage(e.target.value);
          location.reload();
        }}
      >
        {UI_LANGUAGES.map((l) => (
          <option key={l} value={l}>
            {LANGUAGE_NAMES[l] ?? l}
          </option>
        ))}
      </select>
    </label>
  );
}

// ---------------------------------------------------------------------------
// Sign in / register
// ---------------------------------------------------------------------------

function AuthDialog({ mode, onMode, onClose }: { mode: "login" | "register"; onMode: (m: "login" | "register") => void; onClose: () => void }) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  return (
    <div className="lp-auth-veil" onClick={onClose}>
      <div className="lp-auth" role="dialog" aria-modal onClick={(e) => e.stopPropagation()}>
        <button className="lp-auth-close" onClick={onClose} aria-label={t("close")}>
          ×
        </button>
        <img className="lp-auth-m" src="/brand/m.png" alt="" width={64} height={64} />
        <AuthPanel mode={mode} onMode={onMode} />
      </div>
    </div>
  );
}

function authMessage(err: unknown): string {
  if (err instanceof AuthError) {
    switch (err.code) {
      case "invalid-email":
        return t("errInvalidEmail");
      case "wrong-password":
      case "user-not-found":
        return t("errWrongPassword");
      case "email-in-use":
        return t("errEmailInUse");
      case "weak-password":
        return t("errWeakPassword");
      case "too-many":
        return t("errTooMany");
      case "network":
        return t("errNetwork");
    }
  }
  return t("errTryAgain");
}

function AuthPanel({ mode, onMode }: { mode: "login" | "register"; onMode: (m: "login" | "register") => void }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ kind: "error" | "info"; text: string } | null>(null);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    setMsg(null);
    try {
      if (mode === "login") await signIn(email, password);
      else await register(email, password, name);
    } catch (err) {
      setMsg({ kind: "error", text: authMessage(err) });
    } finally {
      setBusy(false);
    }
  };

  // only to sign in: accounts are created with an email (the iOS app creates them with Apple too)
  const apple = async () => {
    if (busy) return;
    setBusy(true);
    setMsg(null);
    try {
      await signInWithApple();
    } catch (err) {
      if (!(err instanceof AuthError && err.code === "cancelled")) setMsg({ kind: "error", text: authMessage(err) });
    } finally {
      setBusy(false);
    }
  };

  const forgot = async () => {
    if (!email.trim()) {
      setMsg({ kind: "error", text: t("errInvalidEmail") });
      return;
    }
    try {
      await resetPassword(email);
      setMsg({ kind: "info", text: t("resetSent") });
    } catch (err) {
      setMsg({ kind: "error", text: authMessage(err) });
    }
  };

  return (
    <form className="auth-panel" onSubmit={submit}>
      <h2>{mode === "login" ? t("login") : t("createAccount")}</h2>
      {mode === "register" && <input dir="auto" className="text-input" value={name} onChange={(e) => setName(e.target.value)} placeholder={t("name")} autoComplete="name" required aria-label={t("name")} />}
      <input className="text-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder={t("email")} autoComplete="email" required aria-label={t("email")} autoFocus />
      <input
        className="text-input"
        type="password"
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        placeholder={t("password")}
        autoComplete={mode === "login" ? "current-password" : "new-password"}
        minLength={6}
        required
        aria-label={t("password")}
      />
      {msg && <p className={msg.kind === "error" ? "error" : "info"}>{msg.text}</p>}
      <div className="auth-actions">
        <button className="btn" type="submit" disabled={busy}>
          {busy ? "…" : mode === "login" ? t("login") : t("createAccount")}
        </button>
        {mode === "login" ? (
          <>
            <button type="button" className="link-btn" onClick={() => void forgot()}>
              {t("forgotPassword")}
            </button>
            <button type="button" className="link-btn" onClick={() => onMode("register")}>
              {t("noAccount")}
            </button>
          </>
        ) : (
          <button type="button" className="link-btn" onClick={() => onMode("login")}>
            {t("haveAccount")}
          </button>
        )}
      </div>
      {mode === "login" && (
        <>
          <div className="auth-or" role="presentation" />
          <AppleButton onClick={() => void apple()} disabled={busy} />
        </>
      )}
    </form>
  );
}

